import { createContext, useContext } from 'react';
import { FoodMakerMenu } from './MenuData';        // The food maker's menu data context

// ---> Define a type for the partner's data

// The type for the DeliverySchedule parameter
type TimeSlot = [string, string];           // e.g., ['09:00', '18:00']
type DaySchedule = TimeSlot[];              // list of time slots
type WeekSchedule = { [day: number]: DaySchedule };
type YearSchedule = { [week: number]: WeekSchedule };

type LocationSchedule = {
  delivery_conditions: string;
  average_delivery_time?: number | null;
  is_deliver_on_time: boolean;
  is_deliver_to_apartment: boolean;
  is_delivery_ASAP_only: boolean;
  is_cashless_terminal_available?: boolean | null;
  is_delivery_available?: boolean | null;
  delivery_unavailable_notification_text_code?: string | null;
  schedule?: { [year: number]: YearSchedule } | null;
};

type DeliverySchedule = { [location: string]: LocationSchedule };

type DeliveryZoneCircle = {
  center: [number, number];
  radius_km: number;
};

type DeliveryZoneDefinition = {
  price: number;
  free_delivery_amount: number;
  name: Record<string, string>;
  coordinates?: [number, number][];
  circle_zone?: DeliveryZoneCircle;
};

type DeliveryZones = Record<string, DeliveryZoneDefinition>;

type SocialMediaLinks = {
  telegram?: string | null;
  viber?: string | null;
  instagram?: string | null;
  whatsapp?: string | null;
  facebook?: string | null;
};

type HtmlSectionDefinition = {
  html_template: string;
  lang_text_codes: string[];
  properties: string[];
};

type HtmlPageDefinition = Record<string, HtmlSectionDefinition>;

type DailyScheduleDay = {
  local_open?: string | null;
  local_close?: string | null;
  delivery_start?: string | null;
  delivery_end?: string | null;
};

type DailyScheduleLocationSettings = {
  is_cashless_terminal_available?: boolean | null;
};

type DailySchedule = {
  schedule_description_text_code?: string | null;
  [key: string]: DailyScheduleDay | DailyScheduleLocationSettings | string | null | undefined;
};

export type ProductCardViewMode = 'compact' | 'grid' | 'list';

type MenuFrontendPageParams = {
  do_product_parameters_show?: boolean | null;
  default_product_view?: ProductCardViewMode | string | null;
  loyalty_card_description_code?: string | null;
  show_loyalty_card_number?: boolean | null;
};

type FrontendPagesParam = {
  Menu?: MenuFrontendPageParams | null;
  [pageName: string]: unknown;
};

export type PartnerDataType = {
  id: number;
  name_code: string;
  h_supplier_type: string | null;
  is_active: boolean;
  is_stop_working_temporary: boolean;
  /** Only false hides the informational closed-hours notices; null/missing shows them. */
  is_show_partner_close_notification?: boolean | null;
  stop_working_notice_code: string;
  management_channel: string | null;
  management_channel_id: string | null;
  management_channel_languages: string | null;
  support_contact_name: string | null;
  support_contact_link: string | null;
  support_contact_channel_id: string | null;
  contact_phone: string | null;
  email: string | null;
  pickup_address: string;
  pickup_address_link: string;
  pickup_point: [number, number];             // The geo coordinates [42.098992, 19.098462]
  location: string;
  delivery_zones: DeliveryZones | null;
  order_statuses: string[];
  schedule_description: string | null;
  daly_schedule?: DailySchedule;
  daily_schedule?: DailySchedule;
  delivery_schedule: DeliverySchedule | null;
  color_style: string | null;
  logotype: string;
  photos: string[];
  socialmedia_links: SocialMediaLinks;
  short_description_code: string;
  html_page: HtmlPageDefinition | null;
  food_maker_key: string;
  menu: FoodMakerMenu;
  own_telegram_bot_name: string | null;
  welcome_msg_code: string | null;
  action_button_code: string;
  is_menu: boolean;
  is_order: boolean;
  is_delivery: boolean;
  is_white_label: boolean;
  is_receipt_with_quantity: boolean;
  delivery_order_min_amount: number;
  couriers: string[];
  use_partner_delivery_zones: boolean;
  frontend_pages_param?: FrontendPagesParam | null;
};

export type PartnerDataRefreshContextType = () => Promise<PartnerDataType | null>;

/**
 * Enables the optional checkout loyalty-card field only for an explicit
 * backend boolean. String and numeric lookalikes remain disabled so malformed
 * partner configuration cannot expose a field unintentionally.
 */
export const isMenuLoyaltyCardNumberEnabled = (
  partnerData: PartnerDataType | null | undefined,
): boolean => partnerData?.frontend_pages_param?.Menu?.show_loyalty_card_number === true;

// The context may be null before data is loaded

const PartnerDataContext = createContext<PartnerDataType | null>(null);
export const PartnerDataRefreshContext = createContext<PartnerDataRefreshContextType | null>(null);

export const usePartnerData = () => useContext(PartnerDataContext);
export const usePartnerDataRefresh = () => useContext(PartnerDataRefreshContext);

export default PartnerDataContext;
